import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { ReactNode } from 'react';

interface IntercomSdkBindings {
  hide: () => void;
  show: () => void;
  onHide: (callback: () => void) => void;
  onShow: (callback: () => void) => void;
}

interface IntercomContextType {
  isMessengerVisible: boolean;
  showMessenger: () => void;
  hideMessengerButton: () => void;
}

function noop() {
  // No-op fallback when IntercomProvider is absent (e.g. tests)
}

const FALLBACK_CONTEXT: IntercomContextType = {
  isMessengerVisible: false,
  showMessenger: noop,
  hideMessengerButton: noop,
};

const IntercomContext = createContext<IntercomContextType | undefined>(undefined);

let sdkBindings: IntercomSdkBindings | null = null;
let pendingHideButton = false;
let pendingShowMessenger = false;
let sdkVisibilityListenersRegistered = false;
let visibilitySubscribers: ((visible: boolean) => void)[] = [];

function notifyVisibilitySubscribers(visible: boolean): void {
  for (const subscriber of visibilitySubscribers) {
    subscriber(visible);
  }
}

function safeInvokeSdk(fn: () => void, label: string): void {
  try {
    fn();
  } catch (error) {
    console.warn(`Intercom SDK call failed (${label})`, error);
  }
}

export function registerIntercomListeners(bindings: IntercomSdkBindings): void {
  sdkBindings = bindings;
  if (!sdkVisibilityListenersRegistered) {
    safeInvokeSdk(
      () => bindings.onShow(() => notifyVisibilitySubscribers(true)),
      'onShow.register',
    );
    safeInvokeSdk(
      () => bindings.onHide(() => notifyVisibilitySubscribers(false)),
      'onHide.register',
    );
    sdkVisibilityListenersRegistered = true;
  }
  if (pendingHideButton) {
    safeInvokeSdk(() => bindings.hide(), 'flush.hide');
    pendingHideButton = false;
  }
  if (pendingShowMessenger) {
    safeInvokeSdk(() => bindings.show(), 'flush.show');
    pendingShowMessenger = false;
  }
}

export function clearIntercomListeners(): void {
  sdkBindings = null;
  pendingHideButton = false;
  pendingShowMessenger = false;
}

export function __resetIntercomContextForTests(): void {
  clearIntercomListeners();
  sdkVisibilityListenersRegistered = false;
  visibilitySubscribers = [];
}

export const useIntercomContext = () => {
  const context = useContext(IntercomContext);
  return context ?? FALLBACK_CONTEXT;
};

interface IntercomProviderProps {
  readonly children: ReactNode;
}

/**
 * Provider that manages Intercom messenger visibility state
 * Hides the messenger button by default and shows it when user clicks "Get Help"
 */
export function IntercomProvider({ children }: IntercomProviderProps) {
  const [isMessengerVisible, setIsMessengerVisible] = useState(false);

  useEffect(() => {
    const onChange = (visible: boolean) => setIsMessengerVisible(visible);
    visibilitySubscribers.push(onChange);
    return () => {
      visibilitySubscribers = visibilitySubscribers.filter((subscriber) => subscriber !== onChange);
    };
  }, []);

  const showMessenger = useCallback(() => {
    if (sdkBindings) {
      const bindings = sdkBindings;
      safeInvokeSdk(() => bindings.show(), 'showMessenger');
    } else {
      pendingShowMessenger = true;
    }
  }, []);

  const hideMessengerButton = useCallback(() => {
    if (sdkBindings) {
      const bindings = sdkBindings;
      safeInvokeSdk(() => bindings.hide(), 'hideMessengerButton');
    } else {
      pendingHideButton = true;
    }
  }, []);

  const value = useMemo(
    () => ({
      isMessengerVisible,
      showMessenger,
      hideMessengerButton,
    }),
    [isMessengerVisible, showMessenger, hideMessengerButton],
  );

  return <IntercomContext.Provider value={value}>{children}</IntercomContext.Provider>;
}
